import { Component, ElementRef, inject, Input, Type, ViewChild } from '@angular/core';
import Chart from 'chart.js/auto';
import { ChartConfiguration, ChartData, ChartOptions, ChartType, TooltipItem } from 'chart.js';
import { MainComponent } from '../../../pages/main/main.component';
import { BeNeo4jService } from '../../../services/be-neo4j.service';
import { SharedService } from '../../../services/shared.service';
import { ShapeBounding } from '../../../clases/ShapeBounding';
import { TimelineComponent } from '../../timeline/timeline.component';
import { CanvasRenderer } from '../../../abstract/CanvasRenderer';
import { NotifyDialogService } from '../../../services/notify-dialog.service';
import { TimeUnit } from '../../../enums/TimeUnit';
import { HeaderValues } from '../../../interfaces/HeaderValues';
import { GraphDialogService } from '../../../services/graph-dialog.service';
import { ERROR } from '../../../shared/strings';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { AbstractShape } from '../../../abstract/Shape';
import { SimulationGraph, SimulationGraphPoint, SimulationGraphSeries, SimulationReturnValues } from '../../../interfaces/Simulation';
import { MatDialog } from '@angular/material/dialog';
import { SimulationGraphsComponent } from '../../dialogs/simulation-graphs/simulation-graphs.component';
import { SimulationReportDialogComponent, renderSimulationReportMarkdown } from '../../dialogs/simulation-report-dialog/simulation-report-dialog.component';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GlobalMetricsComponent } from '../../global-metrics/global-metrics.component';
import { CenterCanvasComponent } from '../../buttons/center-canvas/center-canvas.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DataViewer } from '../../../abstract/DataViewer';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataViewActionsComponent } from '../data-view-actions/data-view-actions.component';
import { generateCurrentDate, generateUUID } from '../../../utils/utils';

@Component({
  selector: 'simulation-tab',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    GlobalMetricsComponent,
    MatIconModule,
    MatButtonModule,
    CenterCanvasComponent,
    SimulationOverviewComponent,
    MatTooltipModule,
    DataViewActionsComponent
  ],
  providers: [{ provide: DataViewer, useExisting: SimulationComponent }],
  templateUrl: './simulation.component.html',
  styleUrl: './simulation.component.scss'
})
export class SimulationComponent extends CanvasRenderer {
  constructor(
    protected override beNeo4jService: BeNeo4jService,
    protected override sharedService: SharedService,
    private notifyDialogService: NotifyDialogService,
    private graphDialogService: GraphDialogService
  ) { super(beNeo4jService, sharedService); }

  ERROR = ERROR;

  DataViewer: SimulationComponent = this;

  @ViewChild('canvas', { static: true })
  set canvasRef(value: ElementRef<HTMLCanvasElement>) {
    let canvas: HTMLCanvasElement = value.nativeElement;
    this.canvas = canvas;
    this._canvasBounding = new ShapeBounding(0, 0, canvas.width, canvas.height);
  }

  @Input()
  override set appComponent(value: MainComponent) {
    this._appComponent = value;
  }

  override get appComponent(): MainComponent {
    return this._appComponent;
  }

  @Input()
  set containerElement(value: HTMLElement) {
    this._containerElement = value;
  }

  get containerElement(): HTMLElement {
    return this._containerElement;
  }

  @ViewChild('simOverview') simOverview!: SimulationOverviewComponent;

  get agentSimulationSnapshot() { return this.simOverview?.getSimulationSnapshot(); }

  timeValues: { name: string, enum: TimeUnit }[] = [
    { name: "Weeks", enum: TimeUnit.WEEKS },
    { name: "Days", enum: TimeUnit.DAY },
    { name: "Hours", enum: TimeUnit.HOUR },
    { name: "minutes", enum: TimeUnit.MINUTES }
  ];

  dialog = inject(MatDialog);

  currentSimulationHeaderValues: HeaderValues[] = [];
  simulationReturnValues: SimulationReturnValues | undefined;
  isExportingSimulationPdf = false;

  setSimulationValues = (values: SimulationReturnValues)=> {
    console.log(values);
    this.currentSimulationHeaderValues = [
      { position: 0, label: "Avg Cycle Time", value: values.CYCLE_TIME.avg_cycle_time } as HeaderValues,
      { position: 1, label: "Max Cycle Time", value: values.CYCLE_TIME.max_cycle_time } as HeaderValues,
      { position: 2, label: "Min Cycle Time", value: values.CYCLE_TIME.min_cycle_time } as HeaderValues,
      { position: 3, label: "Customers Finished", value: values.CYCLE_TIME.customers_finished } as HeaderValues,
      { position: 3, label: "Primary Bottleneck", value: values.BOTTLENECK[0].task_name } as HeaderValues,
      { position: 3, label: "Secondary Bottleneck", value: values.BOTTLENECK[1].task_name } as HeaderValues,
      // { position: 3, label: "Tertiary Bottleneck", value: values.BOTTLENECK[2].task_name } as HeaderValues
    ];

    this.simulationReturnValues = values;
  }

  showSimulationGraph() {
    const dialogRef = this.dialog.open(SimulationGraphsComponent, {
      data: this.simulationReturnValues!.GRAPHS,
      panelClass: 'full-dialog',
    });
    // dialogRef.afterClosed();
  }

  isSimulationReportDialogOpen() {
    return this.isDialogOpen(SimulationReportDialogComponent);
  }

  isSimulationGraphDialogOpen() {
    return this.isDialogOpen(SimulationGraphsComponent);
  }

  private isDialogOpen<T>(component: Type<T>) {
    return this.dialog.openDialogs.some((dialogRef) => dialogRef.componentInstance instanceof component);
  }

  async exportSimulationPdf() {
    const report = this.simOverview?.simulationReport;
    const graphs = this.simulationReturnValues?.GRAPHS || [];

    if (!report || !this.simulationReturnValues || this.isExportingSimulationPdf) {
      return;
    }

    this.isExportingSimulationPdf = true;

    try {
      const reportCanvas = await this.renderReportCapture(report);
      const graphsCanvas = await this.renderGraphsCapture(graphs);
      const pdfBlob = this.createPdfFromCanvases([reportCanvas, graphsCanvas]);
      const modelName = (this.savedDocument.name_value || 'simulation').replace(/[^a-z0-9-_]+/gi, '_');
      this.downloadBlob(
        pdfBlob,
        `DXC_TWS_SIMR_[${modelName}]_${generateCurrentDate()}_${generateUUID()}.pdf`
      );
    } catch (error) {
      this.notifyDialogService.openDialog('Export Simulation PDF', 'Could not create the PDF: ' + error);
    } finally {
      this.isExportingSimulationPdf = false;
    }
  }

  private async renderReportCapture(report: string): Promise<HTMLCanvasElement> {
    const width = 1200;
    const host = document.createElement('div');
    host.style.cssText = [
      'position:fixed',
      'left:-10000px',
      'top:0',
      `width:${width}px`,
      'padding:0',
      'background:#102027',
      'color:#f6fbff',
      'font-family:Oxanium, Roboto, Helvetica Neue, sans-serif'
    ].join(';');
    host.innerHTML = this.getReportCaptureHtml(renderSimulationReportMarkdown(report));
    document.body.appendChild(host);
    await this.waitForPaint();

    const height = Math.ceil(host.scrollHeight);
    const canvas = await this.renderElementAsCanvas(host, width, height);
    host.remove();

    return canvas;
  }

  private getReportCaptureHtml(reportHtml: string): string {
    return `
      <section xmlns="http://www.w3.org/1999/xhtml" style="box-sizing:border-box;width:1200px;padding:42px 54px;background:linear-gradient(145deg,rgba(78,119,255,.22),rgba(0,188,212,.12)),#102027;color:#f6fbff;font-family:Oxanium, Roboto, Helvetica Neue, sans-serif;">
        <style>
          *{box-sizing:border-box;font-family:inherit} h1,h2,h3,h4,h5,h6{margin:1.15em 0 .45em;color:#9ee7ff;line-height:1.08}
          h1:first-child,h2:first-child,h3:first-child{margin-top:0} h1{font-size:38px} h2{font-size:31px} h3{font-size:25px}
          p,li,blockquote{font-size:20px;line-height:1.55} ul,ol{margin:0 0 18px;padding-left:28px} li+li{margin-top:7px}
          blockquote{margin:20px 0;padding:14px 18px;border-left:5px solid #9ee7ff;background:rgba(0,0,0,.24)}
          code{padding:2px 7px;border-radius:4px;background:rgba(0,0,0,.28);color:#9ee7ff;font-family:Courier New,monospace;font-size:.92em}
          pre{margin:20px 0;padding:18px;overflow:hidden;border:1px solid rgba(158,231,255,.22);border-radius:8px;background:rgba(0,0,0,.34);white-space:pre-wrap}
          table{width:100%;border-collapse:collapse;background:rgba(0,0,0,.16)} th,td{padding:12px 14px;border-bottom:1px solid rgba(158,231,255,.16);text-align:left;vertical-align:top;font-size:18px}
          th{color:#9ee7ff;font-weight:700;background:rgba(0,0,0,.25)} tr:last-child td{border-bottom:0} a,strong{color:#9ee7ff}
          .simulation-report-dialog__table-wrap{margin:20px 0;overflow:hidden;border:1px solid rgba(158,231,255,.2);border-radius:8px}
        </style>
        <header style="padding-bottom:24px;margin-bottom:24px;border-bottom:1px solid rgba(246,251,255,.22);">
          <div style="font-size:15px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#9ee7ff;">Simulation Report</div>
          <div style="margin-top:6px;font-size:40px;font-weight:700;line-height:1;">Run Summary</div>
        </header>
        <article>${reportHtml}</article>
      </section>`;
  }

  private async renderElementAsCanvas(element: HTMLElement, width: number, height: number): Promise<HTMLCanvasElement> {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <foreignObject width="100%" height="100%">${element.innerHTML}</foreignObject>
      </svg>`;
    const image = await this.loadImage('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg));
    const canvas = document.createElement('canvas');
    canvas.width = width * 2;
    canvas.height = height * 2;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#102027';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(2, 2);
    ctx.drawImage(image, 0, 0);

    return canvas;
  }

  private async renderGraphsCapture(graphs: SimulationGraph[]): Promise<HTMLCanvasElement> {
    const width = 1200;
    const chartHeight = 470;
    const gap = 34;
    const padding = 54;
    const chartWidth = width - (padding * 2) - 36;
    const graphBlocks = graphs.map((graph) => {
      const valueGroups = this.getGraphValueGroups(graph);
      const valuesHeight = this.getGraphValueGroupsHeight(valueGroups);

      return {
        graph,
        valueGroups,
        height: 58 + chartHeight + valuesHeight + 24
      };
    });
    const height = Math.max(
      360,
      padding + 44 + graphBlocks.reduce((total, block) => total + block.height + gap, 0) + padding
    );
    const canvas = document.createElement('canvas');
    canvas.width = width * 2;
    canvas.height = height * 2;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(2, 2);
    ctx.fillStyle = '#102027';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#9ee7ff';
    ctx.font = '700 40px Arial';
    ctx.fillText('Simulation Graphs', padding, padding);

    if (!graphs.length) {
      ctx.fillStyle = '#f6fbff';
      ctx.font = '24px Arial';
      ctx.fillText('No graphs available for this simulation.', padding, padding + 58);
      return canvas;
    }

    let top = padding + 44;
    for (const block of graphBlocks) {
      const graph = block.graph;
      const chartCanvas = await this.renderSingleGraph(graph, chartWidth, chartHeight);

      ctx.fillStyle = 'rgba(0,0,0,.18)';
      this.roundRect(ctx, padding, top, width - (padding * 2), block.height, 10);
      ctx.fill();
      ctx.fillStyle = '#f6fbff';
      ctx.font = '700 25px Arial';
      ctx.fillText(graph.title, padding + 24, top + 38);
      ctx.drawImage(chartCanvas, padding + 18, top + 58, chartWidth, chartHeight);
      this.drawGraphValueGroups(
        ctx,
        block.valueGroups,
        padding + 24,
        top + 58 + chartHeight + 18,
        width - (padding * 2) - 48
      );
      top += block.height + gap;
    }

    return canvas;
  }

  private async renderSingleGraph(graph: SimulationGraph, width: number, height: number): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const chartType = this.getChartType(graph);
    const chart = new Chart(canvas, {
      type: chartType,
      data: this.buildChartData(graph),
      options: this.buildChartOptions(graph, chartType)
    } as ChartConfiguration);

    chart.update();
    await this.waitForPaint();
    const output = document.createElement('canvas');
    output.width = width * 2;
    output.height = height * 2;
    const outputCtx = output.getContext('2d')!;
    outputCtx.fillStyle = '#102027';
    outputCtx.fillRect(0, 0, output.width, output.height);
    outputCtx.drawImage(canvas, 0, 0, output.width, output.height);
    chart.destroy();

    return output;
  }

  private getChartType(graph: SimulationGraph): ChartType {
    return graph.chart_type === 'scatter' ? 'scatter' : 'bar';
  }

  private buildChartData(graph: SimulationGraph): ChartData {
    switch (graph.chart_type) {
      case 'scatter':
        return {
          datasets: graph.series.map((series: SimulationGraphSeries) => ({
            label: series.name.toString(),
            data: (series.points ?? []).map((point: SimulationGraphPoint) => ({ ...point, x: point.x, y: point.y })),
            showLine: false,
            pointRadius: 5,
            pointHoverRadius: 7
          }))
        };
      case 'histogram': {
        const values = graph.series.flatMap((series: SimulationGraphSeries) => series.values ?? []);
        const bins = this.createHistogramBins(values);
        return {
          labels: bins.map((bin) => bin.label),
          datasets: [{ label: graph.series[0]?.name ?? graph.title, data: bins.map((bin) => bin.count) }]
        };
      }
      case 'bar':
      case 'grouped_bar':
      default:
        return {
          labels: graph.x_axis.values ?? [],
          datasets: graph.series.map((series: SimulationGraphSeries) => ({
            label: series.name.toString(),
            data: series.values ?? [],
            fill: false,
            tension: 0.4
          }))
        };
    }
  }

  private buildChartOptions(graph: SimulationGraph, chartType: ChartType): ChartOptions {
    const chartText = '#f6fbff';
    const chartGrid = 'rgba(246,251,255,.18)';
    const options: ChartOptions = {
      responsive: false,
      maintainAspectRatio: false,
      devicePixelRatio: 2,
      animation: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: chartText }
        },
        tooltip: { enabled: false }
      },
      interaction: {
        mode: chartType === 'scatter' ? 'nearest' : 'index',
        intersect: chartType === 'scatter'
      },
      scales: {
        x: {
          type: chartType === 'scatter' ? 'linear' : 'category',
          ticks: { color: chartText },
          grid: { color: chartGrid },
          title: { display: true, text: graph.x_axis.label, color: chartText }
        },
        y: {
          ticks: { color: chartText },
          grid: { color: chartGrid },
          title: { display: true, text: graph.y_axis.label, color: chartText }
        }
      }
    };

    if (chartType === 'scatter') {
      options.plugins!.tooltip = {
        enabled: false,
        callbacks: {
          label: (context: TooltipItem<ChartType>) => {
            const point = context.raw as SimulationGraphPoint;
            const label = point.label ?? context.dataset.label ?? '';
            return `${label}: (${point.x}, ${point.y})`;
          }
        }
      };
    }

    return options;
  }

  private createHistogramBins(values: number[]): { label: string; count: number }[] {
    if (values.length === 0) {
      return [];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
      return [{ label: this.formatBinLabel(min, max), count: values.length }];
    }

    const binCount = Math.min(20, Math.max(5, Math.ceil(Math.sqrt(values.length))));
    const binSize = (max - min) / binCount;
    const bins = Array.from({ length: binCount }, (_value, index) => {
      const start = min + (index * binSize);
      const end = index === binCount - 1 ? max : start + binSize;
      return { start, end, label: this.formatBinLabel(start, end), count: 0 };
    });

    values.forEach((value) => {
      const binIndex = value === max ? binCount - 1 : Math.floor((value - min) / binSize);
      bins[binIndex].count += 1;
    });

    return bins.map(({ label, count }) => ({ label, count }));
  }

  private formatBinLabel(start: number, end: number): string {
    return `${this.formatBinValue(start)} - ${this.formatBinValue(end)}`;
  }

  private formatBinValue(value: number): string {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  private getGraphValueGroups(graph: SimulationGraph): { title: string; rows: { name: string; value: string }[] }[] {
    if (graph.chart_type === 'scatter') {
      return graph.series
        .map((series: SimulationGraphSeries) => ({
          title: series.name || 'Values',
          rows: (series.points ?? []).map((point: SimulationGraphPoint) => ({
            name: point.label || series.name,
            value: `(${this.getScatterAxisValue(graph.x_axis.values, point.x)}, ${this.getScatterAxisValue(graph.y_axis.values, point.y)})`
          }))
        }))
        .filter((group) => group.rows.length);
    }

    if (graph.chart_type === 'histogram') {
      const values = graph.series.flatMap((series: SimulationGraphSeries) => series.values ?? []);
      return [{
        title: graph.series[0]?.name || 'Values',
        rows: this.createHistogramBins(values).map((bin) => ({
          name: bin.label,
          value: bin.count.toString()
        }))
      }];
    }

    const labels = graph.x_axis.values ?? [];
    return graph.series
      .map((series: SimulationGraphSeries) => ({
        title: graph.series.length > 1 ? series.name : 'Values',
        rows: (series.values ?? []).map((value: number, index: number) => {
          const label = labels[index] ?? `Item ${index + 1}`;

          return {
            name: label.toString(),
            value: this.formatGraphNumber(value)
          };
        })
      }))
      .filter((group) => group.rows.length);
  }

  private getGraphValueGroupsHeight(groups: { title: string; rows: { name: string; value: string }[] }[]): number {
    return groups.reduce((height, group) => {
      const rowCount = Math.ceil(group.rows.length / 3);
      return height + 38 + (rowCount * 34) + 14;
    }, 0);
  }

  private drawGraphValueGroups(
    ctx: CanvasRenderingContext2D,
    groups: { title: string; rows: { name: string; value: string }[] }[],
    x: number,
    y: number,
    width: number
  ) {
    if (!groups.length) {
      return;
    }

    let tableY = y;

    groups.forEach((group) => {
      this.drawGraphValueRows(ctx, group.title, group.rows, x, tableY, width);
      tableY += this.getGraphValueGroupsHeight([group]);
    });
  }

  private drawGraphValueRows(
    ctx: CanvasRenderingContext2D,
    title: string,
    rows: { name: string; value: string }[],
    x: number,
    y: number,
    width: number
  ) {
    const columns = 3;
    const columnGap = 18;
    const rowHeight = 34;
    const columnWidth = (width - (columnGap * (columns - 1))) / columns;

    ctx.fillStyle = '#9ee7ff';
    ctx.font = '700 18px Arial';
    ctx.fillText(title, x, y);

    rows.forEach((row, index) => {
      const column = index % columns;
      const rowIndex = Math.floor(index / columns);
      const cellX = x + column * (columnWidth + columnGap);
      const cellY = y + 20 + rowIndex * rowHeight;

      ctx.fillStyle = 'rgba(255,255,255,.06)';
      this.roundRect(ctx, cellX, cellY, columnWidth, 26, 5);
      ctx.fill();
      ctx.fillStyle = '#f6fbff';
      ctx.font = '500 16px Arial';
      ctx.fillText(this.truncateCanvasText(ctx, row.name, columnWidth - 94), cellX + 10, cellY + 18);
      ctx.fillStyle = '#9ee7ff';
      ctx.font = '700 16px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(row.value, cellX + columnWidth - 10, cellY + 18);
      ctx.textAlign = 'left';
    });
  }

  private formatGraphNumber(value: number): string {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  private getScatterAxisValue(axisValues: Array<string | number> | undefined, coordinate: number): string {
    if (!axisValues?.length) {
      return this.formatGraphNumber(coordinate);
    }

    const directValue = Number.isInteger(coordinate) ? axisValues[coordinate] : undefined;
    const oneBasedValue = Number.isInteger(coordinate) && coordinate > 0 ? axisValues[coordinate - 1] : undefined;
    const resolvedValue = directValue ?? oneBasedValue ?? coordinate;

    return typeof resolvedValue === 'number' ? this.formatGraphNumber(resolvedValue) : resolvedValue.toString();
  }

  private truncateCanvasText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (ctx.measureText(text).width <= maxWidth) {
      return text;
    }

    let truncated = text;
    while (truncated.length > 1 && ctx.measureText(`${truncated}...`).width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }

    return `${truncated}...`;
  }

  private createPdfFromCanvases(canvases: HTMLCanvasElement[]): Blob {
    const pages = canvases.map((canvas) => {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.96);
      const binary = atob(dataUrl.split(',')[1]);
      const pageWidth = 595.28;
      const pageHeight = pageWidth * (canvas.height / canvas.width);

      return {
        binary,
        width: pageWidth,
        height: pageHeight,
        imageWidth: canvas.width,
        imageHeight: canvas.height
      };
    });
    const objects: string[] = [];
    const pageRefs: number[] = [];
    const imageRefs: number[] = [];
    const contentRefs: number[] = [];

    pages.forEach((page) => {
      const imageObjectNumber = objects.length + 1;
      objects.push(`<< /Type /XObject /Subtype /Image /Width ${page.imageWidth} /Height ${page.imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.binary.length} >>\nstream\n${page.binary}\nendstream`);
      imageRefs.push(imageObjectNumber);

      const content = `q\n${page.width.toFixed(2)} 0 0 ${page.height.toFixed(2)} 0 0 cm\n/Im${imageRefs.length} Do\nQ`;
      const contentObjectNumber = objects.length + 1;
      objects.push(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
      contentRefs.push(contentObjectNumber);
    });

    pages.forEach((page, index) => {
      const pageObjectNumber = objects.length + 1;
      const imageName = `Im${index + 1}`;
      objects.push(`<< /Type /Page /Parent 0 0 R /MediaBox [0 0 ${page.width.toFixed(2)} ${page.height.toFixed(2)}] /Resources << /XObject << /${imageName} ${imageRefs[index]} 0 R >> >> /Contents ${contentRefs[index]} 0 R >>`);
      pageRefs.push(pageObjectNumber);
    });

    const pagesObjectNumber = objects.length + 1;
    const pagesKids = pageRefs.map((ref) => `${ref} 0 R`).join(' ');
    objects.push(`<< /Type /Pages /Kids [${pagesKids}] /Count ${pageRefs.length} >>`);

    pageRefs.forEach((pageRef) => {
      objects[pageRef - 1] = objects[pageRef - 1].replace('/Parent 0 0 R', `/Parent ${pagesObjectNumber} 0 R`);
    });

    const catalogObjectNumber = objects.length + 1;
    objects.push(`<< /Type /Catalog /Pages ${pagesObjectNumber} 0 R >>`);

    let pdf = '%PDF-1.4\n';
    const offsets = [0];
    objects.forEach((object, index) => {
      offsets.push(pdf.length);
      pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
    });

    const xrefOffset = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    for (let index = 1; index < offsets.length; index++) {
      pdf += `${offsets[index].toString().padStart(10, '0')} 00000 n \n`;
    }
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogObjectNumber} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

    const bytes = new Uint8Array(pdf.length);
    for (let index = 0; index < pdf.length; index++) {
      bytes[index] = pdf.charCodeAt(index) & 0xff;
    }

    return new Blob([bytes], { type: 'application/pdf' });
  }

  private async loadImage(src: string): Promise<HTMLImageElement> {
    const image = new Image();
    image.decoding = 'async';
    image.src = src;

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image render failed'));
    });

    return image;
  }

  private waitForPaint(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  }

  private downloadBlob(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  ngAfterViewInit(): void {
    this._isViewEditable = false;
    setTimeout(() => {
      this.setupCanvas();
      this.bindEvents();
    }, 0)

    document.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });

    this.simOverview.overviewToggle.subscribe((value: boolean) => {
      this.draw();
    });
  }

  override onMouseMove(e: MouseEvent) {
    this.isShapeHover = this.canvasHelper.onHoverShape(e, this.canvasItems, this.hoveredShape);
    this.isConnectionHover = this.canvasHelper.onHoverConnection(e, this.connections, this.hoveredConnection);
    this.canvasHelper.paneCamera(e);
    this.draw();
  }


  override onMouseUp() {
    this.checkFocus();
    this.canvasHelper.stopCameraPanning();
    this.draw();
  }

  protected override afterFetchingContent(): void {
    super.afterFetchingContent();
    if(this.savedDocument.model_UUID)this.showMetricsButton = true;
  }

  protected override errorReporting(title: string, message: string): void {
    this.notifyDialogService.openDialog(title, message);
  }
}
